import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import SuperadminLayout, { DarkCard } from "./SuperadminLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MagnifyingGlass, DotsThreeVertical, UserSwitch, Key, Clock, Prohibit, ShieldWarning, LockKeyOpen } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

function statusColor(s) {
  return {
    active: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    trialing: "bg-sky-500/20 text-sky-300 border-sky-500/30",
    past_due: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    canceled: "bg-red-500/20 text-red-300 border-red-500/30",
  }[s] || "bg-white/10 text-white/60 border-white/20";
}

export default function SuperadminUsers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // { type, user }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (role !== "all") params.role = role;
      if (status !== "all") params.status = status;
      const { data } = await api.get("/superadmin/users", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  }, [q, role, status]);

  useEffect(() => { load(); }, [load]);

  const impersonate = async (user, reason) => {
    try {
      await api.post(`/superadmin/users/${user.user_id}/impersonate`, { reason });
      toast.success(`Session ouverte en tant que ${user.email}`);
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation impossible");
    }
  };

  const patchUser = async (userId, changes, reason) => {
    try {
      await api.patch(`/superadmin/users/${userId}`, { ...changes, reason });
      toast.success("Utilisateur mis à jour");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const sendReset = async (user) => {
    try {
      await api.post(`/superadmin/users/${user.user_id}/send-reset`, { reason: "Support" });
      toast.success(`Email de réinitialisation envoyé à ${user.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec envoi");
    }
  };

  const extendTrial = async (user, days) => {
    try {
      const { data } = await api.post(`/superadmin/users/${user.user_id}/extend-trial`, { days, reason: "Support commercial" });
      toast.success(`Essai prolongé jusqu'au ${new Date(data.trial_ends_at).toLocaleDateString("fr-FR")}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const reset2FA = async (user, reason) => {
    try {
      const { data } = await api.post(`/superadmin/users/${user.user_id}/reset-2fa`, { reason });
      toast.success(
        data.email_sent
          ? `2FA réinitialisée. Email envoyé à ${user.email}.`
          : `2FA réinitialisée (l'email de notification n'a pas pu être envoyé).`
      );
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec réinitialisation 2FA");
    }
  };

  const regenBackupCodes = async (user, reason) => {
    try {
      const { data } = await api.post(`/superadmin/users/${user.user_id}/regenerate-backup-codes`, { reason });
      // Show the codes ONCE, then close.
      setDialog({ type: "backup-codes-shown", user, codes: data.backup_codes, emailSent: data.email_sent });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec régénération codes de secours");
    }
  };

  const openUnlockDialog = async (user) => {
    try {
      const { data } = await api.get(`/superadmin/users/${user.user_id}/lockout-status`);
      setDialog({ type: "unlock-login", user, status: data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chargement du statut impossible");
    }
  };

  const unlockLogin = async (user, reason) => {
    try {
      const { data } = await api.post(`/superadmin/users/${user.user_id}/unlock-login`, { reason });
      toast.success(
        data.cleared > 0
          ? `${data.cleared} verrou${data.cleared > 1 ? 's' : ''} effacé${data.cleared > 1 ? 's' : ''} pour ${user.email}`
          : `Aucun verrou actif sur ${user.email}`
      );
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec déverrouillage");
    }
  };

  return (
    <SuperadminLayout title="Utilisateurs" actions={<span className="text-sm text-[#8494A8]">{total} résultats</span>}>
      <DarkCard className="p-4 mb-6" data-testid="users-filters">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8494A8]" />
            <Input
              placeholder="Email ou nom…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-[#081221] border-white/10 text-white placeholder:text-[#5F758F]"
              data-testid="users-search"
            />
          </div>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="bg-[#081221] border-white/10 text-white" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les rôles</SelectItem>
              <SelectItem value="admin">Praticien (admin)</SelectItem>
              <SelectItem value="org_admin">Org admin</SelectItem>
              <SelectItem value="assistant">Assistant</SelectItem>
              <SelectItem value="research">Recherche</SelectItem>
              <SelectItem value="superadmin">Superadmin</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="bg-[#081221] border-white/10 text-white" data-testid="users-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="trialing">Essai</SelectItem>
              <SelectItem value="active">Actif</SelectItem>
              <SelectItem value="past_due">Paiement échoué</SelectItem>
              <SelectItem value="canceled">Résilié</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </DarkCard>

      <DarkCard className="overflow-hidden" data-testid="users-table">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-[#8494A8]">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Utilisateur</th>
              <th className="text-left px-4 py-3 font-medium">Rôle</th>
              <th className="text-left px-4 py-3 font-medium">Statut</th>
              <th className="text-left px-4 py-3 font-medium">Cabinet / Org</th>
              <th className="text-left px-4 py-3 font-medium">Créé</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-8 text-center text-[#8494A8]">Chargement…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#8494A8]">Aucun utilisateur</td></tr>}
            {items.map((u) => (
              <tr key={u.user_id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`user-row-${u.user_id}`}>
                <td className="px-4 py-3">
                  <p className="text-white font-medium">{u.name || "—"}</p>
                  <p className="text-[#8494A8] text-xs break-all">{u.email}</p>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 flex-wrap">
                    <Badge className="bg-white/10 text-white/80 hover:bg-white/10">{u.role || "admin"}</Badge>
                    {u.is_superadmin && <Badge className="bg-[#E4572E]/25 text-[#FFB59E] border border-[#E4572E]/40 hover:bg-[#E4572E]/25">Superadmin</Badge>}
                    {u.is_disabled && <Badge className="bg-red-500/25 text-red-300 border border-red-500/40 hover:bg-red-500/25">Désactivé</Badge>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Badge className={`${statusColor(u.subscription_status)} border`}>{u.subscription_status || "—"}</Badge>
                </td>
                <td className="px-4 py-3 text-[#B7C4D6] text-xs">
                  <p>{u.cabinet_name || "—"}</p>
                  <p className="text-[#8494A8] font-mono">{u.organization_id?.slice(0, 12) || "—"}</p>
                </td>
                <td className="px-4 py-3 text-[#8494A8] text-xs">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-[#B7C4D6] hover:bg-white/10 hover:text-white h-8 w-8 p-0" data-testid={`user-actions-${u.user_id}`}>
                        <DotsThreeVertical size={18} weight="bold" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#0F2136] border-white/10 text-white">
                      <DropdownMenuItem onClick={() => setDialog({ type: "impersonate", user: u })} className="focus:bg-white/10 focus:text-white">
                        <UserSwitch size={14} className="mr-2" /> Se connecter comme
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => sendReset(u)} className="focus:bg-white/10 focus:text-white">
                        <Key size={14} className="mr-2" /> Envoyer un reset password
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => extendTrial(u, 30)} className="focus:bg-white/10 focus:text-white">
                        <Clock size={14} className="mr-2" /> Prolonger l'essai (+30 j)
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setDialog({ type: "reset-2fa", user: u })}
                        disabled={!u.totp_enabled && !u.totp_secret}
                        className="focus:bg-white/10 focus:text-white text-amber-300 data-[disabled]:text-white/30"
                        data-testid={`user-reset-2fa-${u.user_id}`}
                      >
                        <ShieldWarning size={14} className="mr-2" /> Réinitialiser le 2FA
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setDialog({ type: "regen-backup", user: u })}
                        disabled={!u.totp_enabled}
                        className="focus:bg-white/10 focus:text-white text-amber-300 data-[disabled]:text-white/30"
                        data-testid={`user-regen-backup-${u.user_id}`}
                      >
                        <Key size={14} className="mr-2" /> Régénérer codes de secours
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => openUnlockDialog(u)}
                        className="focus:bg-white/10 focus:text-white text-sky-300"
                        data-testid={`user-unlock-login-${u.user_id}`}
                      >
                        <LockKeyOpen size={14} className="mr-2" /> Débloquer le compte (brute-force)
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem onClick={() => setDialog({ type: "edit", user: u })} className="focus:bg-white/10 focus:text-white">
                        Modifier (rôle, plan, statut…)
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => patchUser(u.user_id, { is_disabled: !u.is_disabled }, "Bascule activation via backoffice")}
                        className="focus:bg-white/10 focus:text-white text-red-400"
                      >
                        <Prohibit size={14} className="mr-2" /> {u.is_disabled ? "Réactiver" : "Désactiver"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DarkCard>

      {dialog?.type === "impersonate" && (
        <ImpersonateDialog user={dialog.user} onConfirm={(reason) => impersonate(dialog.user, reason)} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "edit" && (
        <EditUserDialog user={dialog.user} onSave={(changes, reason) => patchUser(dialog.user.user_id, changes, reason)} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "reset-2fa" && (
        <Reset2FADialog user={dialog.user} onConfirm={(reason) => reset2FA(dialog.user, reason)} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "regen-backup" && (
        <RegenBackupDialog user={dialog.user} onConfirm={(reason) => regenBackupCodes(dialog.user, reason)} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "backup-codes-shown" && (
        <BackupCodesShown user={dialog.user} codes={dialog.codes} emailSent={dialog.emailSent} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "unlock-login" && (
        <UnlockLoginDialog user={dialog.user} status={dialog.status} onConfirm={(reason) => unlockLogin(dialog.user, reason)} onClose={() => setDialog(null)} />
      )}
    </SuperadminLayout>
  );
}

function UnlockLoginDialog({ user, status, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  const attempts = status?.attempts || [];
  const anyLocked = !!status?.any_locked;
  const kindLabel = { login: "Login (mot de passe)", totp: "2FA (code TOTP)", forgot: "Mot de passe oublié" };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F2136] border-white/10 text-white sm:max-w-lg" data-testid="unlock-login-dialog">
        <DialogHeader><DialogTitle>Débloquer le compte de {user.email}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {attempts.length === 0 ? (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-200">
              Aucune tentative échouée récente pour ce compte. Rien à effacer.
            </div>
          ) : (
            <>
              <div className={`rounded-lg border p-3 text-sm ${anyLocked ? "border-red-500/40 bg-red-500/10 text-red-200" : "border-amber-500/30 bg-amber-500/10 text-amber-200"}`}>
                <p className="font-semibold mb-1">
                  {anyLocked ? "Compte actuellement verrouillé" : "Historique de tentatives échouées"}
                </p>
                <p className={anyLocked ? "text-red-200/80" : "text-amber-200/80"}>
                  Confirmer effacera <strong>{attempts.length}</strong> entrée{attempts.length > 1 ? "s" : ""} de la table <code className="text-xs">login_attempts</code> (login classique + 2FA + mot de passe oublié). Le compte pourra se reconnecter immédiatement.
                </p>
              </div>
              <div className="rounded-lg bg-[#081221] border border-white/10 p-3 text-xs font-mono max-h-40 overflow-y-auto" data-testid="unlock-attempts-list">
                {attempts.map((a) => (
                  <div key={a.identifier} className="flex items-center justify-between gap-3 py-1 border-b border-white/5 last:border-0">
                    <span className="text-[#B7C4D6]">{kindLabel[a.kind] || a.kind}</span>
                    <span className="text-[#8494A8]">{a.failed}× échec</span>
                    {a.locked_now
                      ? <Badge className="bg-red-500/25 text-red-300 border border-red-500/40 hover:bg-red-500/25 text-[10px]">verrouillé</Badge>
                      : <span className="text-[#5F758F] text-[10px]">expiré</span>}
                  </div>
                ))}
              </div>
            </>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Motif (RGPD)</Label>
            <Input
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Support ticket #3312 — utilisateur légitime"
              className="mt-2 bg-[#081221] border-white/10 text-white placeholder:text-[#5F758F]"
              data-testid="unlock-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#B7C4D6] hover:bg-white/10 hover:text-white">Annuler</Button>
          <Button
            disabled={reason.trim().length < 3}
            onClick={() => onConfirm(reason.trim())}
            className="bg-[#3A8FA5] hover:bg-[#164E5F] text-white"
            data-testid="unlock-confirm"
          >
            Débloquer maintenant
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RegenBackupDialog({ user, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F2136] border-white/10 text-white" data-testid="regen-backup-dialog">
        <DialogHeader><DialogTitle>Régénérer les codes de secours de {user.email}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
            <p className="font-semibold mb-1">10 nouveaux codes de secours seront générés.</p>
            <p className="text-amber-200/80">
              L'authenticator (Google/Authy) de l'utilisateur <strong>continue à fonctionner</strong> — seuls les codes papier sont renouvelés.
              Les anciens codes deviennent immédiatement invalides. Les nouveaux codes vous seront affichés <strong>une seule fois</strong> — transmettez-les par un canal sécurisé (téléphone, en main propre).
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Motif (RGPD)</Label>
            <Input
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Ticket #8912 — codes papier perdus"
              className="mt-2 bg-[#081221] border-white/10 text-white placeholder:text-[#5F758F]"
              data-testid="regen-backup-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#B7C4D6] hover:bg-white/10 hover:text-white">Annuler</Button>
          <Button
            disabled={reason.trim().length < 3}
            onClick={() => onConfirm(reason.trim())}
            className="bg-[#E4572E] hover:bg-[#F27551] text-white"
            data-testid="regen-backup-confirm"
          >
            Régénérer les 10 codes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BackupCodesShown({ user, codes, emailSent, onClose }) {
  const [ack, setAck] = useState(false);

  const download = () => {
    const content =
      `Codes de secours 2FA — Questiomed\n` +
      `Compte : ${user.email}\n` +
      `Régénérés le : ${new Date().toLocaleString("fr-FR")}\n\n` +
      `IMPORTANT : Chaque code est à usage unique. Les anciens codes sont invalides.\n\n` +
      codes.map((c, i) => `  ${String(i + 1).padStart(2, "0")}.  ${c}`).join("\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `questiomed-backup-codes-${user.email}-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    setAck(true);
    toast.success("Codes téléchargés — transmettez-les à l'utilisateur par un canal sécurisé");
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setAck(true);
      toast.success("Codes copiés");
    } catch { toast.error("Copie impossible — utilisez le téléchargement"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && ack && onClose()}>
      <DialogContent className="bg-[#0F2136] border-white/10 text-white sm:max-w-lg" data-testid="backup-codes-shown">
        <DialogHeader><DialogTitle>Nouveaux codes de secours — {user.email}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
            <p className="font-semibold text-red-300 mb-1">Cette liste ne sera plus jamais affichée.</p>
            Copiez ou téléchargez les codes maintenant, puis transmettez-les à l'utilisateur par un canal sécurisé (téléphone vérifié, en main propre). Ne les envoyez pas par email.
            {emailSent && <><br /><span className="text-red-200/80">Un email d'information (sans les codes) a été envoyé à l'utilisateur.</span></>}
          </div>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-[#081221] rounded-xl p-4 border border-white/10" data-testid="regen-backup-codes-list">
            {codes.map((c, i) => (
              <div key={c} className="flex items-center gap-2 py-1">
                <span className="text-[#5F758F] text-xs w-5">{String(i + 1).padStart(2, "0")}.</span>
                <code className="text-[#B0C97F] tracking-wider">{c}</code>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={download} className="flex-1 bg-[#E4572E] hover:bg-[#F27551] text-white" data-testid="regen-backup-download">Télécharger .txt</Button>
            <Button onClick={copyAll} variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10" data-testid="regen-backup-copy">Copier</Button>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={onClose} disabled={!ack}
            className="bg-[#3A8FA5] hover:bg-[#164E5F] text-white disabled:opacity-40"
            data-testid="regen-backup-done"
          >
            {ack ? "Fermer" : "Téléchargez ou copiez d'abord…"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Reset2FADialog({ user, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const canConfirm = reason.trim().length >= 3 && confirmText === "RESET";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F2136] border-white/10 text-white" data-testid="reset-2fa-dialog">
        <DialogHeader><DialogTitle>Réinitialiser le 2FA de {user.email}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
            <p className="font-semibold mb-1">Action de dépannage — à n'utiliser qu'en cas de perte du téléphone <em>ET</em> des codes de secours.</p>
            <p className="text-amber-200/80">
              L'utilisateur pourra se reconnecter <strong>sans 2FA</strong> à la prochaine tentative.
              {user.is_superadmin ? " En tant que superadmin, il devra reconfigurer immédiatement Google Authenticator." : " Il pourra le reconfigurer depuis ses paramètres."}
              {" "}Un email de notification lui sera envoyé, et l'action sera tracée dans l'audit RGPD.
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Motif (RGPD)</Label>
            <Input
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Ticket #4521 — perte téléphone + codes secours"
              className="mt-2 bg-[#081221] border-white/10 text-white placeholder:text-[#5F758F]"
              data-testid="reset-2fa-reason"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Tapez <span className="text-amber-300 font-mono">RESET</span> pour confirmer</Label>
            <Input
              value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
              placeholder="RESET"
              className="mt-2 bg-[#081221] border-white/10 text-white placeholder:text-[#5F758F] font-mono"
              data-testid="reset-2fa-confirm-text"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#B7C4D6] hover:bg-white/10 hover:text-white">Annuler</Button>
          <Button
            disabled={!canConfirm}
            onClick={() => onConfirm(reason.trim())}
            className="bg-[#E4572E] hover:bg-[#F27551] text-white"
            data-testid="reset-2fa-confirm"
          >
            Réinitialiser le 2FA
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImpersonateDialog({ user, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F2136] border-white/10 text-white" data-testid="impersonate-dialog">
        <DialogHeader><DialogTitle>Se connecter comme {user.email}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-[#B7C4D6]">
            La session sera limitée à <strong>15 minutes</strong>. Un bandeau rouge sera visible en permanence.
            Toutes les actions seront tracées avec votre identité de superadmin.
          </p>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Motif (RGPD)</Label>
            <Input
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Support ticket #1234 — investigation bug affichage"
              className="mt-2 bg-[#081221] border-white/10 text-white placeholder:text-[#5F758F]"
              data-testid="impersonate-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#B7C4D6] hover:bg-white/10 hover:text-white">Annuler</Button>
          <Button
            disabled={reason.trim().length < 3}
            onClick={() => onConfirm(reason.trim())}
            className="bg-[#E4572E] hover:bg-[#F27551] text-white"
            data-testid="impersonate-confirm"
          >
            Se connecter comme cet utilisateur
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, onSave, onClose }) {
  const [role, setRole] = useState(user.role || "admin");
  const [statusVal, setStatusVal] = useState(user.subscription_status || "trialing");
  const [plan, setPlan] = useState(user.plan || "base");
  const [reason, setReason] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F2136] border-white/10 text-white" data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle>Modifier {user.email}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Rôle</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-2 bg-[#081221] border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">admin (praticien)</SelectItem>
                <SelectItem value="org_admin">org_admin</SelectItem>
                <SelectItem value="assistant">assistant</SelectItem>
                <SelectItem value="research">research</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Statut abonnement</Label>
            <Select value={statusVal} onValueChange={setStatusVal}>
              <SelectTrigger className="mt-2 bg-[#081221] border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="trialing">trialing</SelectItem>
                <SelectItem value="active">active</SelectItem>
                <SelectItem value="past_due">past_due</SelectItem>
                <SelectItem value="canceled">canceled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Plan</Label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger className="mt-2 bg-[#081221] border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="base">base (Standard)</SelectItem>
                <SelectItem value="base_yearly">base_yearly</SelectItem>
                <SelectItem value="premium">premium</SelectItem>
                <SelectItem value="premium_yearly">premium_yearly</SelectItem>
                <SelectItem value="establishment_seat_monthly">establishment_seat_monthly</SelectItem>
                <SelectItem value="establishment_seat_yearly">establishment_seat_yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Motif</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Support commercial…"
              className="mt-2 bg-[#081221] border-white/10 text-white placeholder:text-[#5F758F]"
              data-testid="edit-user-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#B7C4D6] hover:bg-white/10 hover:text-white">Annuler</Button>
          <Button
            onClick={() => onSave({ role, subscription_status: statusVal, plan }, reason)}
            className="bg-[#E4572E] hover:bg-[#F27551] text-white"
            data-testid="edit-user-save"
          >
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
